import { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Divider,
  Dropdown,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Combobox,
  Option,
  OverlayDrawer,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowDown16Regular,
  ArrowUp16Regular,
  CalendarAdd24Regular,
  CalendarLtr24Regular,
  ChevronRight16Regular,
  Dismiss24Regular,
  Document24Regular,
  LockClosed24Regular,
  Mail16Regular,
  Open16Regular,
} from "@fluentui/react-icons";
import { useApp } from "../context/AppContext";
import { LeaveBalance, LeaveRequest, LeaveStatus } from "../models/leaveModels";
import {
  daysBetween,
  formatDateList,
  formatDisplayDate,
  formatLongDate,
  isoToLocalDate,
  parseISODate,
  todayISO,
} from "../utils/dateUtils";
import { leaveTypeColor } from "../utils/leaveTypeColors";
import kationMark from "../assets/brand/kation-mark.png";

const RING_C = 2 * Math.PI * 52;

type BadgeColor = "success" | "warning" | "danger" | "subtle" | "informative";
const STATUS_BADGE: Record<LeaveStatus, BadgeColor> = {
  Draft: "informative",
  Pending: "warning",
  Approved: "success",
  "Auto-Approved": "success",
  Rejected: "danger",
  Cancelled: "subtle",
};
const STATUS_STRIPE: Record<LeaveStatus, string> = {
  Draft: "#8A94A0",
  Pending: "#C08A00",
  Approved: "#1B9254",
  "Auto-Approved": "#1B9254",
  Rejected: "#C0392B",
  Cancelled: "#8A94A0",
};
const STATUS_ORDER: Record<LeaveStatus, number> = {
  Pending: 0,
  Approved: 1,
  "Auto-Approved": 1,
  Rejected: 2,
  Cancelled: 3,
  Draft: 4,
};

const STATUS_FILTERS: (LeaveStatus | "All")[] = ["All", "Pending", "Approved", "Rejected", "Cancelled"];

type SortKey = "dates" | "days" | "status" | "filed" | null;
type SortDir = "asc" | "desc";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalL },

  banner: {
    position: "relative", overflow: "hidden", isolation: "isolate",
    borderRadius: tokens.borderRadiusXLarge, padding: "28px 30px", color: "#EAF2FB",
    backgroundImage: "radial-gradient(120% 150% at 10% 8%, #155aa5 0%, transparent 46%), linear-gradient(140deg, #0A2A4A, #0e3e6e)",
    "@media (max-width: 640px)": { padding: "24px 22px" },
  },
  bannerGrid: { position: "absolute", inset: 0, zIndex: -1, backgroundImage: "linear-gradient(rgba(157,182,208,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(157,182,208,0.16) 1px, transparent 1px)", backgroundSize: "40px 40px", maskImage: "radial-gradient(120% 90% at 18% 8%, #000 42%, transparent 82%)", WebkitMaskImage: "radial-gradient(120% 90% at 18% 8%, #000 42%, transparent 82%)", opacity: 0.45 },
  bannerBlob: { position: "absolute", right: "-60px", top: "-70px", width: "280px", height: "280px", borderRadius: "50%", filter: "blur(50px)", opacity: 0.45, zIndex: -1, backgroundImage: "radial-gradient(circle, #2E9BE0, transparent 68%)" },
  bannerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "26px", flexWrap: "wrap" },
  greetTitle: { color: "#fff", fontSize: "1.6rem", fontWeight: 720, letterSpacing: "-0.02em", margin: 0 },
  greetLine: { color: "#AFC4DA", fontSize: "0.95rem", margin: "6px 0 0", maxWidth: "46ch" },
  greetLineStrong: { color: "#fff", fontWeight: 650 },
  notePill: { display: "inline-flex", alignItems: "center", gap: "7px", marginTop: "14px", fontSize: "0.8rem", fontWeight: 600, padding: "6px 12px", borderRadius: tokens.borderRadiusCircular, backgroundColor: "rgba(246,166,35,0.16)", border: "1px solid rgba(246,166,35,0.4)", color: "#FFD79A" },
  cta: { marginTop: "18px" },
  kpis: { display: "flex", gap: "12px", "@media (max-width: 640px)": { width: "100%" } },
  kpi: { backgroundColor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: tokens.borderRadiusLarge, padding: "14px 18px", minWidth: "116px", "@media (max-width: 640px)": { flexGrow: 1, minWidth: 0 } },
  kpiN: { fontSize: "1.5rem", fontWeight: 720, letterSpacing: "-0.02em", color: "#fff", lineHeight: 1.05, fontVariantNumeric: "tabular-nums" },
  kpiNDate: { fontSize: "1.12rem" },
  kpiNSmall: { fontSize: "0.78rem", fontWeight: 600, color: "#AFC4DA" },
  kpiK: { fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "#9DB6D0", marginTop: "8px" },

  hero: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: tokens.spacingHorizontalM, "@media (max-width: 900px)": { gridTemplateColumns: "1fr" } },
  card: { position: "relative", overflow: "hidden", display: "flex", gap: "20px", alignItems: "center", padding: "22px 24px", backgroundColor: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusXLarge, boxShadow: tokens.shadow4 },
  cardAcc: { position: "absolute", left: 0, top: 0, bottom: 0, width: "4px" },
  ringWrap: { position: "relative", width: "104px", height: "104px", flexShrink: 0 },
  ring: { width: "104px", height: "104px", transform: "rotate(-90deg)" },
  ringLabel: { position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },
  ringNum: { fontSize: "1.95rem", fontWeight: 720, letterSpacing: "-0.03em", lineHeight: 1, fontVariantNumeric: "tabular-nums" },
  ringCap: { fontSize: "0.62rem", color: tokens.colorNeutralForeground3, marginTop: "3px", textTransform: "uppercase", letterSpacing: "0.05em" },
  balTitle: { display: "flex", alignItems: "center", gap: "8px", fontWeight: 650, fontSize: "1.02rem", flexWrap: "wrap" },
  balSub: { color: tokens.colorNeutralForeground3, fontSize: "0.82rem", margin: "2px 0 14px" },
  dot: { width: "9px", height: "9px", borderRadius: "50%", flexShrink: 0, display: "inline-block" },
  breakdown: { display: "flex", flexDirection: "column", gap: "7px" },
  brow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", fontSize: "0.83rem" },
  bl: { color: tokens.colorNeutralForeground2, display: "flex", alignItems: "center", gap: "7px" },
  bi: { width: "7px", height: "7px", borderRadius: "2px" },
  bv: { fontWeight: 650, fontVariantNumeric: "tabular-nums" },
  lockCircle: { width: "104px", height: "104px", borderRadius: "50%", border: `2px dashed ${tokens.colorNeutralStroke2}`, display: "grid", placeItems: "center", color: tokens.colorNeutralForeground4 },
  lockMsg: { fontSize: "0.82rem", color: tokens.colorNeutralForeground3, maxWidth: "30ch" },

  reqHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacingHorizontalM, flexWrap: "wrap" },
  reqTitle: { display: "flex", alignItems: "center", gap: "9px", fontSize: "1.12rem", fontWeight: 680, margin: 0 },
  countPill: { fontSize: "0.72rem", fontWeight: 650, color: tokens.colorNeutralForeground3, backgroundColor: tokens.colorNeutralBackground3, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusCircular, padding: "2px 9px", fontVariantNumeric: "tabular-nums" },
  reqTools: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, flexWrap: "wrap" },
  search: { minWidth: "210px" },
  seg: { display: "inline-flex", backgroundColor: tokens.colorNeutralBackground3, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, padding: "3px", gap: "2px", flexWrap: "wrap" },
  segBtn: { border: "none", background: "none", fontFamily: "inherit", fontSize: "0.82rem", color: tokens.colorNeutralForeground3, padding: "6px 12px", borderRadius: tokens.borderRadiusSmall, cursor: "pointer" },
  segBtnOn: { backgroundColor: tokens.colorNeutralBackground1, color: tokens.colorNeutralForeground1, fontWeight: 600, boxShadow: tokens.shadow2 },

  list: { backgroundColor: tokens.colorNeutralBackground1, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusXLarge, boxShadow: tokens.shadow4, overflow: "hidden" },
  lhead: { display: "grid", gridTemplateColumns: "48px minmax(0, 1.7fr) 96px 118px 108px 168px", alignItems: "center", gap: "14px", padding: "0 20px", boxSizing: "border-box", height: "42px", backgroundColor: tokens.colorNeutralBackground2, borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  hcell: { fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.07em", color: tokens.colorNeutralForeground3, fontWeight: 600 },
  hsort: { display: "inline-flex", alignItems: "center", gap: "4px", border: "none", background: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.07em", color: tokens.colorNeutralForeground3, fontWeight: 600, ":hover": { color: tokens.colorNeutralForeground1 } },
  hsortOn: { color: tokens.colorBrandForeground1 },
  hsortIcon: { width: "13px", height: "13px" },
  lrow: {
    position: "relative", display: "grid", gridTemplateColumns: "48px minmax(0, 1.7fr) 96px 118px 108px 168px", alignItems: "center", gap: "14px", padding: "0 20px", minHeight: "64px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, cursor: "pointer", textAlign: "left", width: "100%", boxSizing: "border-box", background: "none", fontFamily: "inherit", color: "inherit",
    ":hover": { backgroundColor: tokens.colorNeutralBackground2 },
    ":focus-visible": { outline: `2px solid ${tokens.colorBrandStroke1}`, outlineOffset: "-2px" },
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr auto", rowGap: "6px", padding: "14px 18px" },
  },
  lrowLast: { borderBottom: "none" },
  tchip: { fontWeight: 650, fontSize: "0.88rem" },
  colMin0: { minWidth: 0 },
  dCell: { fontWeight: 600, fontSize: "0.9rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  relCell: { fontSize: "0.75rem", color: tokens.colorNeutralForeground3, marginTop: "1px" },
  daysCell: { display: "flex", flexDirection: "column", justifyContent: "center", lineHeight: 1.2 },
  daysNum: { fontWeight: 680, fontVariantNumeric: "tabular-nums", fontSize: "0.9rem" },
  daysSub: { fontSize: "0.72rem", color: "#9A6A00", fontWeight: 600, marginTop: "1px" },
  reasonCell: { minWidth: 0, fontSize: "0.86rem", color: tokens.colorNeutralForeground2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  filedCell: { fontSize: "0.86rem", color: tokens.colorNeutralForeground2, fontVariantNumeric: "tabular-nums" },
  actionCell: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px", "@media (max-width: 900px)": { gridColumn: "1 / -1", justifyContent: "flex-start", marginTop: "2px" } },
  abtn: { display: "inline-flex", alignItems: "center", gap: "4px", borderRadius: tokens.borderRadiusMedium, padding: "5px 9px", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer", border: `1px solid ${tokens.colorNeutralStroke1}`, backgroundColor: tokens.colorNeutralBackground1, color: tokens.colorNeutralForeground2, whiteSpace: "nowrap", transition: "all .12s ease" },
  abtnRemind: { color: tokens.colorBrandForeground1, border: `1px solid ${tokens.colorBrandStroke1}`, ":hover": { backgroundColor: tokens.colorBrandBackground2, border: `1px solid ${tokens.colorBrandStroke1}` } },
  abtnCancel: { ":hover": { color: tokens.colorPaletteRedForeground1, border: `1px solid ${tokens.colorPaletteRedBorder1}`, backgroundColor: tokens.colorPaletteRedBackground1 } },
  abtnIcon: { width: "13px", height: "13px" },
  viewChev: { color: tokens.colorNeutralForeground4 },
  cType: { "@media (max-width: 900px)": { gridColumn: "1", gridRow: "1" } },
  cStatus: { "@media (max-width: 900px)": { gridColumn: "2", gridRow: "1", justifySelf: "end" } },
  cFull: { "@media (max-width: 900px)": { gridColumn: "1 / -1" } },
  cMobHide: { "@media (max-width: 900px)": { display: "none" } },

  empty: { padding: "56px 24px", textAlign: "center" },
  emptyIc: { width: "56px", height: "56px", borderRadius: tokens.borderRadiusLarge, display: "grid", placeItems: "center", margin: "0 auto 16px", backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground1 },
  emptyH: { fontSize: "1.05rem", fontWeight: 680, margin: "0 0 6px" },
  emptyP: { color: tokens.colorNeutralForeground3, fontSize: "0.9rem", margin: "0 auto 18px", maxWidth: "38ch" },
  simpleEmpty: { padding: "40px 24px", textAlign: "center", color: tokens.colorNeutralForeground3 },

  // drawer
  drawerTitleWrap: { display: "flex", flexDirection: "column", gap: "6px" },
  drawerBrand: { display: "flex", alignItems: "center", gap: "8px", fontSize: "0.74rem", fontWeight: 650, letterSpacing: "0.04em", color: tokens.colorNeutralForeground3, marginBottom: "2px" },
  drawerBrandMark: { width: "20px", height: "20px", borderRadius: tokens.borderRadiusSmall, display: "block" },
  attachCard: { display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "inherit", border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, color: tokens.colorNeutralForeground1, backgroundColor: tokens.colorNeutralBackground1, ":hover": { backgroundColor: tokens.colorNeutralBackground2, border: `1px solid ${tokens.colorNeutralStroke1}` } },
  attachIcon: { color: tokens.colorBrandForeground1, flexShrink: 0 },
  attachName: { flexGrow: 1, fontSize: "0.88rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  previewSurface: { maxWidth: "920px", width: "92vw" },
  previewImg: { maxWidth: "100%", maxHeight: "72vh", display: "block", margin: "0 auto", borderRadius: tokens.borderRadiusMedium },
  previewFrame: { width: "100%", height: "72vh", border: "none", borderRadius: tokens.borderRadiusMedium },
  previewCenter: { display: "grid", placeItems: "center", minHeight: "220px", padding: tokens.spacingVerticalXL, color: tokens.colorNeutralForeground2 },
  drawerType: { display: "flex", alignItems: "center", gap: "9px", fontSize: "1.05rem", fontWeight: 680 },
  drawerDates: { fontSize: "0.9rem", color: tokens.colorNeutralForeground2, fontWeight: 500 },
  dsection: { display: "flex", flexDirection: "column", gap: "2px", padding: "14px 0" },
  dsectionLabel: { fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em", color: tokens.colorNeutralForeground3, fontWeight: 650, marginBottom: "6px" },
  field: { display: "flex", justifyContent: "space-between", gap: "16px", padding: "5px 0", fontSize: "0.88rem" },
  fieldK: { color: tokens.colorNeutralForeground3, flexShrink: 0 },
  fieldV: { fontWeight: 600, textAlign: "right", wordBreak: "break-word" },
  person: { display: "inline-flex", alignItems: "center", gap: "8px", minWidth: 0 },
  personName: { fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  reasonBox: { fontSize: "0.9rem", lineHeight: 1.5, color: tokens.colorNeutralForeground1, backgroundColor: tokens.colorNeutralBackground2, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, padding: "12px 14px" },
  timeline: { display: "flex", flexDirection: "column", gap: "0" },
  tlItem: { display: "flex", gap: "12px", position: "relative", paddingBottom: "16px" },
  tlDot: { width: "10px", height: "10px", borderRadius: "50%", marginTop: "4px", flexShrink: 0, zIndex: 1 },
  tlLine: { position: "absolute", left: "4px", top: "12px", bottom: 0, width: "2px", backgroundColor: tokens.colorNeutralStroke2 },
  tlBody: { display: "flex", flexDirection: "column", gap: "1px" },
  tlTitle: { fontSize: "0.88rem", fontWeight: 600 },
  tlDate: { fontSize: "0.78rem", color: tokens.colorNeutralForeground3, fontVariantNumeric: "tabular-nums" },
});

function canCancel(r: LeaveRequest): boolean {
  if (r.status === "Pending") return true;
  if (r.status === "Approved" || r.status === "Auto-Approved") return r.startDate > todayISO();
  return false;
}
function timeGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
function compactRange(startIso: string, endIso: string): string {
  const s = parseISODate(startIso), e = parseISODate(endIso);
  const mon = (d: Date) => d.toLocaleDateString("en-US", { month: "short" });
  const day = (d: Date) => String(d.getDate());
  if (startIso === endIso) return `${mon(s)} ${day(s)}`;
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) return `${mon(s)} ${day(s)}–${day(e)}`;
  return `${mon(s)} ${day(s)} – ${mon(e)} ${day(e)}`;
}
function relLabel(r: LeaveRequest): string {
  const t = todayISO();
  const monthYear = parseISODate(r.startDate).toLocaleDateString("en-US", { month: "short", year: "numeric" });
  if (r.endDate < t) return monthYear;
  if (r.startDate <= t && t <= r.endDate) return "ongoing";
  const d = daysBetween(t, r.startDate);
  if (d <= 31) return `in ${d} day${d === 1 ? "" : "s"}`;
  return monthYear;
}

/** True when the person who decided differs from the assigned approver
 * (i.e. a delegate/backup acted). With today's 1:1 routing this is false. */
function decidedByDiffers(r: LeaveRequest): boolean {
  if (!r.approver) return false;
  if (r.approver.toLowerCase() === r.managerEmail.toLowerCase()) return false;
  return prettyName(r.approver).toLowerCase() !== prettyName(r.managerEmail).toLowerCase();
}

/** Classify an attachment by extension for choosing an in-app preview. */
function attachmentKind(nameOrUrl: string): "image" | "pdf" | "other" {
  const ext = nameOrUrl.split("?")[0].split(".").pop()?.toLowerCase() ?? "";
  if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "other";
}

/** Display name from an email local-part, e.g. dona.lim -> "Dona Lim". */
function prettyName(value?: string): string {
  if (!value) return "—";
  if (!value.includes("@")) return value;
  const local = value.split("@")[0];
  const words = local.split(/[._-]+/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1));
  return words.join(" ") || value;
}

export function MyLeaves({ onFileLeave }: { onFileLeave?: () => void }) {
  const s = useStyles();
  const { service, currentUser, employee, year, leaveTypes, refreshSignal, triggerRefresh } = useApp();

  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<LeaveStatus | "All">("All");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [selectedYear, setSelectedYear] = useState(year);
  const [detail, setDetail] = useState<LeaveRequest | null>(null);
  const [photoCache, setPhotoCache] = useState<Record<string, string | null>>({});
  const fetchedPhotos = useRef<Set<string>>(new Set());
  const [preview, setPreview] = useState<{ webUrl: string; name: string; kind: "image" | "pdf" | "other"; src: string | null; loading: boolean } | null>(null);
  const [cancelTarget, setCancelTarget] = useState<LeaveRequest | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      service.getMyLeaveRequests(currentUser.email),
      service.getMyBalances(currentUser.email, selectedYear),
    ]).then(([reqs, bals]) => {
      if (cancelled) return;
      setRequests(reqs);
      setBalances(bals);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [service, currentUser.email, selectedYear, refreshSignal]);

  const typeByCode = useMemo(() => new Map(leaveTypes.map((t) => [t.code, t])), [leaveTypes]);
  const typeTitle = (r: LeaveRequest) => typeByCode.get(r.leaveTypeCode)?.title ?? r.leaveTypeCode;

  const yearRequests = useMemo(
    () => requests.filter((r) => new Date(r.startDate).getFullYear() === selectedYear),
    [requests, selectedYear],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = yearRequests
      .filter((r) => statusFilter === "All" || r.status === statusFilter)
      .filter((r) => {
        if (!q) return true;
        const hay = [r.leaveTypeCode, typeTitle(r), formatDateList(r.leaveDates), r.reason, r.status]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    const withSort = [...filtered];
    if (!sortKey) {
      withSort.sort((a, b) => {
        const pa = a.status === "Pending" ? 0 : 1, pb = b.status === "Pending" ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return a.startDate < b.startDate ? 1 : -1;
      });
    } else {
      const m = sortDir === "asc" ? 1 : -1;
      withSort.sort((a, b) => {
        let av: string | number, bv: string | number;
        if (sortKey === "dates") { av = a.startDate; bv = b.startDate; }
        else if (sortKey === "days") { av = a.workingDays; bv = b.workingDays; }
        else if (sortKey === "filed") { av = a.filedDate ?? ""; bv = b.filedDate ?? ""; }
        else { av = STATUS_ORDER[a.status]; bv = STATUS_ORDER[b.status]; }
        return av < bv ? -1 * m : av > bv ? 1 * m : 0;
      });
    }
    return withSort;
  }, [yearRequests, statusFilter, search, sortKey, sortDir]);

  const pendingByCode = useMemo(() => {
    const m: Record<string, number> = {};
    yearRequests.filter((r) => r.status === "Pending").forEach((r) => {
      m[r.leaveTypeCode] = (m[r.leaveTypeCode] ?? 0) + Math.max(0, r.workingDays - r.lwopDays);
    });
    return m;
  }, [yearRequests]);

  const cards = useMemo(
    () =>
      balances.map((b) => {
        const lt = typeByCode.get(b.leaveTypeCode);
        const locked = !!employee?.probationStatus && lt?.availableDuringProbation === false;
        return { balance: b, title: lt?.title ?? b.leaveTypeCode, color: leaveTypeColor(b.leaveTypeCode), pending: pendingByCode[b.leaveTypeCode] ?? 0, locked };
      }),
    [balances, typeByCode, employee, pendingByCode],
  );

  const available = cards.filter((c) => !c.locked).reduce((a, c) => a + c.balance.remaining, 0);
  const awaiting = yearRequests.filter((r) => r.status === "Pending").length;
  const nextOff = useMemo(() => {
    const t = todayISO();
    const up = yearRequests
      .filter((r) => ["Pending", "Approved", "Auto-Approved"].includes(r.status) && r.endDate >= t)
      .sort((a, b) => (a.startDate < b.startDate ? -1 : 1));
    return up[0] ? compactRange(up[0].startDate, up[0].endDate) : null;
  }, [yearRequests]);

  const years = useMemo(() => {
    const set = new Set<number>([year, year - 1]);
    requests.forEach((r) => set.add(new Date(r.startDate).getFullYear()));
    return Array.from(set).sort((a, b) => b - a);
  }, [requests, year]);

  const firstName = currentUser.name.split(" ")[0];
  const isCurrentYear = selectedYear === year;
  const isPastYear = selectedYear < year;
  const hasBalances = balances.length > 0;

  // Autosuggest values from the user's own requests (reasons, types, statuses).
  const suggestions = useMemo(() => {
    const vals = new Set<string>();
    yearRequests.forEach((r) => {
      const reason = r.reason?.trim();
      if (reason) vals.add(reason);
      vals.add(typeByCode.get(r.leaveTypeCode)?.title ?? r.leaveTypeCode);
      vals.add(r.status);
    });
    const q = search.trim().toLowerCase();
    return Array.from(vals)
      .filter((v) => (q ? v.toLowerCase().includes(q) && v.toLowerCase() !== q : true))
      .slice(0, 8);
  }, [yearRequests, search, typeByCode]);

  // Fetch approver/manager photos for the open request (people chips in the drawer).
  useEffect(() => {
    if (!detail) return;
    [detail.managerEmail, detail.approver].forEach((em) => {
      if (!em || !em.includes("@") || fetchedPhotos.current.has(em)) return;
      fetchedPhotos.current.add(em);
      service.getPhotoUrlForEmail?.(em)
        .then((url) => setPhotoCache((c) => ({ ...c, [em]: url ?? null })))
        .catch(() => setPhotoCache((c) => ({ ...c, [em]: null })));
    });
  }, [detail, service]);

  function toggleSort(key: Exclude<SortKey, null>) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  }

  // A "nudge" opens a pre-filled reminder email to the approver (ManagerEmail).
  // Zero backend — works today. (A fully-automated nudge would be a Power
  // Automate flow watching a reminder field.)
  function remindHref(r: LeaveRequest): string {
    const subject = `Reminder: leave approval pending — ${compactRange(r.startDate, r.endDate)}`;
    const body =
      `Hi,\n\nJust a gentle reminder that my ${typeTitle(r)} request for ${formatDateList(r.leaveDates)} ` +
      `(${r.workingDays} day${r.workingDays > 1 ? "s" : ""}), filed on ${formatDisplayDate(isoToLocalDate(r.filedDate))}, ` +
      `is still awaiting your approval.\n\nThank you!`;
    return `mailto:${r.managerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  async function openPreview(r: LeaveRequest) {
    if (!r.attachmentUrl) return;
    const name = r.attachmentName || decodeURIComponent(r.attachmentUrl.split("/").pop() || "Attachment");
    const kind = attachmentKind(name);
    setPreview({ webUrl: r.attachmentUrl, name, kind, src: null, loading: kind !== "other" });
    if (kind === "other") return; // no inline preview for this type
    const src = (await service.getAttachmentBlobUrl?.(r.attachmentUrl)) ?? null;
    setPreview((p) => (p && p.webUrl === r.attachmentUrl ? { ...p, src, loading: false } : p));
  }

  async function confirmCancel() {
    if (!cancelTarget?.id) return;
    setBusy(true);
    try {
      await service.cancelLeaveRequest(cancelTarget.id);
      setCancelTarget(null);
      setDetail(null);
      triggerRefresh();
    } finally {
      setBusy(false);
    }
  }

  const SortHeader = ({ label, k, extraClass }: { label: string; k: Exclude<SortKey, null>; extraClass?: string }) => (
    <button className={`${s.hsort} ${sortKey === k ? s.hsortOn : ""} ${extraClass ?? ""}`} onClick={() => toggleSort(k)}>
      {label}
      {sortKey === k && (sortDir === "asc" ? <ArrowUp16Regular className={s.hsortIcon} /> : <ArrowDown16Regular className={s.hsortIcon} />)}
    </button>
  );

  // A Microsoft-style person chip: profile photo (or initials) + display name.
  const personChip = (value?: string) => {
    if (!value) return <span className={s.fieldV}>—</span>;
    const name = prettyName(value);
    const photo = value.includes("@") ? photoCache[value] : null;
    return (
      <span className={s.person}>
        <Avatar name={name} image={photo ? { src: photo } : undefined} size={24} color="colorful" />
        <span className={s.personName}>{name}</span>
      </span>
    );
  };

  return (
    <div className={s.root}>
      {/* ---- gradient hero banner ---- */}
      <div className={s.banner}>
        <div className={s.bannerGrid} />
        <div className={s.bannerBlob} />
        <div className={s.bannerRow}>
          <div>
            <h1 className={s.greetTitle}>{timeGreeting()}, {firstName} 👋</h1>
            {hasBalances ? (
              <p className={s.greetLine}>
                You’ve got <span className={s.greetLineStrong}>{available} paid days</span> available
                {isCurrentYear ? " this year" : ` in ${selectedYear}`}.
              </p>
            ) : (
              <p className={s.greetLine}>No leave balance records for {selectedYear}.</p>
            )}
            {hasBalances && available === 0 && isCurrentYear && (
              <span className={s.notePill}>⚠ Paid leave depleted — further leave is unpaid (LWOP)</span>
            )}
            <div className={s.cta}>
              <Button appearance="primary" icon={<Add20Regular />} onClick={onFileLeave} style={{ backgroundColor: "#fff", color: "#0A2A4A" }}>
                File a leave
              </Button>
            </div>
          </div>
          <div className={s.kpis}>
            <div className={s.kpi}><div className={`${s.kpiN} ${nextOff ? s.kpiNDate : ""}`}>{nextOff ?? "None yet"}</div><div className={s.kpiK}>Next time off</div></div>
            <div className={s.kpi}><div className={s.kpiN}>{awaiting} <span className={s.kpiNSmall}>{awaiting === 1 ? "request" : "requests"}</span></div><div className={s.kpiK}>Awaiting approval</div></div>
            <div className={s.kpi}><div className={s.kpiN}>{yearRequests.length}</div><div className={s.kpiK}>Requests this year</div></div>
          </div>
        </div>
      </div>

      {/* ---- balance ring cards ---- */}
      {loading && balances.length === 0 ? (
        <Spinner label="Loading balances…" />
      ) : balances.length === 0 ? null : (
        <div className={s.hero}>
          {cards.map((c) => {
            const b = c.balance;
            if (c.locked) {
              return (
                <div className={s.card} key={b.id ?? b.leaveTypeCode}>
                  <span className={s.cardAcc} style={{ background: c.color }} />
                  <div className={s.ringWrap}><div className={s.lockCircle}><LockClosed24Regular /></div></div>
                  <div>
                    <div className={s.balTitle}><span className={s.dot} style={{ background: c.color }} />{c.title}</div>
                    <div className={s.balSub}>Not available during probation</div>
                    <div className={s.lockMsg}>You can use Vacation Leave once you&rsquo;re regularized.</div>
                  </div>
                </div>
              );
            }
            const ent = b.entitlement || 0;
            const pct = ent > 0 ? Math.min(1, b.used / ent) : 0;
            return (
              <div className={s.card} key={b.id ?? b.leaveTypeCode}>
                <span className={s.cardAcc} style={{ background: c.color }} />
                <div className={s.ringWrap}>
                  <svg className={s.ring} viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="52" fill="none" strokeWidth="11" stroke={tokens.colorNeutralStroke2} />
                    <circle cx="60" cy="60" r="52" fill="none" strokeWidth="11" strokeLinecap="round" stroke={c.color} strokeDasharray={RING_C} strokeDashoffset={RING_C * (1 - pct)} />
                  </svg>
                  <div className={s.ringLabel}><span className={s.ringNum}>{b.remaining}</span><span className={s.ringCap}>days left</span></div>
                </div>
                <div>
                  <div className={s.balTitle}>
                    <span className={s.dot} style={{ background: c.color }} />{c.title}
                    {b.remaining === 0 ? <Badge appearance="tint" color="danger" size="small">Depleted</Badge> : b.remaining <= 2 ? <Badge appearance="tint" color="warning" size="small">Running low</Badge> : null}
                  </div>
                  <div className={s.balSub}>{b.used} of {ent} days used · {selectedYear}</div>
                  <div className={s.breakdown}>
                    <div className={s.brow}><span className={s.bl}><span className={s.bi} style={{ background: c.color }} />Used</span><span className={s.bv}>{b.used}</span></div>
                    <div className={s.brow}><span className={s.bl}><span className={s.bi} style={{ background: c.color, opacity: 0.4 }} />Pending</span><span className={s.bv}>{c.pending}</span></div>
                    <div className={s.brow}><span className={s.bl}><span className={s.bi} style={{ background: tokens.colorNeutralStroke2 }} />Entitlement</span><span className={s.bv}>{ent}</span></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---- requests ---- */}
      <div className={s.reqHead}>
        <h2 className={s.reqTitle}>Requests <span className={s.countPill}>{yearRequests.length}</span></h2>
        <div className={s.reqTools}>
          <Combobox
            className={s.search}
            size="medium"
            freeform
            placeholder="Search requests"
            value={search}
            aria-label="Search requests"
            onChange={(e) => setSearch(e.target.value)}
            onOptionSelect={(_, d) => setSearch(d.optionText ?? "")}
          >
            {suggestions.length === 0 ? (
              <Option key="__none" disabled>No matches</Option>
            ) : (
              suggestions.map((sug) => <Option key={sug}>{sug}</Option>)
            )}
          </Combobox>
          <div className={s.seg}>
            {STATUS_FILTERS.map((st) => (
              <button key={st} className={`${s.segBtn} ${statusFilter === st ? s.segBtnOn : ""}`} onClick={() => setStatusFilter(st)}>{st}</button>
            ))}
          </div>
          <Dropdown size="medium" value={String(selectedYear)} selectedOptions={[String(selectedYear)]} onOptionSelect={(_, d) => setSelectedYear(Number(d.optionValue))} aria-label="Year">
            {years.map((y) => <Option key={y} value={String(y)}>{String(y)}</Option>)}
          </Dropdown>
        </div>
      </div>

      {loading ? (
        <Spinner label="Loading requests…" />
      ) : yearRequests.length === 0 ? (
        <div className={s.list}><div className={s.empty}>
          <div className={s.emptyIc}>{isPastYear ? <CalendarLtr24Regular /> : <CalendarAdd24Regular />}</div>
          {isPastYear ? (
            <>
              <div className={s.emptyH}>No leave requests in {selectedYear}</div>
              <p className={s.emptyP}>There are no records for this year. Switch to {year} to see your current leaves.</p>
            </>
          ) : (
            <>
              <div className={s.emptyH}>No leave requests yet</div>
              <p className={s.emptyP}>When you file a leave, it’ll show up here with its status and dates.</p>
              <Button appearance="primary" icon={<Add20Regular />} onClick={onFileLeave}>File your first leave</Button>
            </>
          )}
        </div></div>
      ) : rows.length === 0 ? (
        <div className={s.list}><div className={s.simpleEmpty}>
          No requests match {search ? `“${search}”` : `this filter`}{search ? "" : ` for ${selectedYear}`}.
        </div></div>
      ) : (
        <div className={s.list}>
          <div className={s.lhead}>
            <span className={s.hcell}>Type</span>
            <SortHeader label="Dates" k="dates" />
            <SortHeader label="Days" k="days" />
            <SortHeader label="Status" k="status" />
            <SortHeader label="Filed" k="filed" extraClass={s.cMobHide} />
            <span className={s.hcell} />
          </div>
          {rows.map((r, i) => (
            <div
              className={`${s.lrow} ${i === rows.length - 1 ? s.lrowLast : ""}`}
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => setDetail(r)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetail(r); } }}
            >
              <div className={s.cType}><span className={s.tchip}>{r.leaveTypeCode}</span></div>
              <div className={`${s.cFull} ${s.colMin0}`}>
                <div className={s.dCell}>{formatDateList(r.leaveDates)}</div>
                <div className={s.relCell}>{relLabel(r)}</div>
              </div>
              <div className={`${s.daysCell} ${s.cFull}`}>
                <span className={s.daysNum}>{r.workingDays} {r.workingDays === 1 ? "day" : "days"}</span>
                {r.lwopDays > 0 && <span className={s.daysSub}>{r.lwopDays} unpaid</span>}
              </div>
              <div className={s.cStatus}><Badge appearance="tint" color={STATUS_BADGE[r.status]}>{r.status}</Badge></div>
              <div className={`${s.filedCell} ${s.cMobHide}`}>{formatDisplayDate(isoToLocalDate(r.filedDate))}</div>
              <div className={s.actionCell}>
                {r.status === "Pending" && (
                  <button className={`${s.abtn} ${s.abtnRemind}`} onClick={(e) => { e.stopPropagation(); window.location.href = remindHref(r); }}>
                    <Mail16Regular className={s.abtnIcon} />Remind
                  </button>
                )}
                {canCancel(r) && (
                  <button className={`${s.abtn} ${s.abtnCancel}`} onClick={(e) => { e.stopPropagation(); setCancelTarget(r); }}>Cancel</button>
                )}
                {r.status !== "Pending" && !canCancel(r) && <ChevronRight16Regular className={s.viewChev} />}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- detail drawer ---- */}
      <OverlayDrawer position="end" size="medium" open={!!detail} onOpenChange={(_, d) => !d.open && setDetail(null)}>
        <DrawerHeader>
          <DrawerHeaderTitle action={<Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={() => setDetail(null)} />}>
            <div className={s.drawerTitleWrap}>
              <div className={s.drawerBrand}>
                <img src={kationMark} className={s.drawerBrandMark} alt="" />
                Kation People
              </div>
              <span className={s.drawerType}>
                <span className={s.dot} style={{ background: leaveTypeColor(detail?.leaveTypeCode) }} />
                {detail ? typeTitle(detail) : ""}
                {detail && <Badge appearance="tint" color={STATUS_BADGE[detail.status]}>{detail.status}</Badge>}
              </span>
              {detail && <span className={s.drawerDates}>{formatDateList(detail.leaveDates)}</span>}
            </div>
          </DrawerHeaderTitle>
        </DrawerHeader>
        {detail && (
          <DrawerBody>
            <div className={s.dsection}>
              <div className={s.dsectionLabel}>Details</div>
              <div className={s.field}><span className={s.fieldK}>Leave type</span><span className={s.fieldV}>{typeTitle(detail)} ({detail.leaveTypeCode})</span></div>
              <div className={s.field}><span className={s.fieldK}>Dates</span><span className={s.fieldV}>{formatDateList(detail.leaveDates)}</span></div>
              <div className={s.field}><span className={s.fieldK}>Total days</span><span className={s.fieldV}>{detail.workingDays}</span></div>
              {detail.lwopDays > 0 && (
                <>
                  <div className={s.field}><span className={s.fieldK}>Paid (deducts balance)</span><span className={s.fieldV}>{detail.workingDays - detail.lwopDays}</span></div>
                  <div className={s.field}><span className={s.fieldK}>Unpaid (LWOP)</span><span className={s.fieldV}>{detail.lwopDays}</span></div>
                </>
              )}
            </div>
            <Divider />
            <div className={s.dsection}>
              <div className={s.dsectionLabel}>Approval</div>
              <div className={s.field}><span className={s.fieldK}>Approver</span>{personChip(detail.managerEmail)}</div>
              {decidedByDiffers(detail) && <div className={s.field}><span className={s.fieldK}>Decided by</span>{personChip(detail.approver)}</div>}
              {detail.decisionNotes && <div className={s.field}><span className={s.fieldK}>Decision note</span><span className={s.fieldV}>{detail.decisionNotes}</span></div>}
            </div>
            <Divider />
            <div className={s.dsection}>
              <div className={s.dsectionLabel}>Reason</div>
              <div className={s.reasonBox}>{detail.reason?.trim() || "No reason provided."}</div>
            </div>
            {detail.attachmentUrl && (
              <>
                <Divider />
                <div className={s.dsection}>
                  <div className={s.dsectionLabel}>Attachment</div>
                  <button type="button" className={s.attachCard} onClick={() => openPreview(detail)}>
                    <Document24Regular className={s.attachIcon} />
                    <span className={s.attachName}>{detail.attachmentName || "View attachment"}</span>
                    <Open16Regular />
                  </button>
                </div>
              </>
            )}
            <Divider />
            <div className={s.dsection}>
              <div className={s.dsectionLabel}>Timeline</div>
              <div className={s.timeline}>
                <div className={s.tlItem}>
                  <span className={s.tlLine} />
                  <span className={s.tlDot} style={{ background: leaveTypeColor(detail.leaveTypeCode) }} />
                  <div className={s.tlBody}><span className={s.tlTitle}>Filed</span><span className={s.tlDate}>{formatLongDate(isoToLocalDate(detail.filedDate)) || "—"}</span></div>
                </div>
                {detail.status === "Pending" ? (
                  <div className={s.tlItem}>
                    <span className={s.tlDot} style={{ background: STATUS_STRIPE.Pending }} />
                    <div className={s.tlBody}><span className={s.tlTitle}>Awaiting approval</span><span className={s.tlDate}>Since {formatLongDate(isoToLocalDate(detail.pendingSince)) || "—"}</span></div>
                  </div>
                ) : (
                  <div className={s.tlItem}>
                    <span className={s.tlDot} style={{ background: STATUS_STRIPE[detail.status] }} />
                    <div className={s.tlBody}><span className={s.tlTitle}>{detail.status}</span><span className={s.tlDate}>{formatLongDate(isoToLocalDate(detail.decisionDate)) || "—"}</span></div>
                  </div>
                )}
              </div>
            </div>
          </DrawerBody>
        )}
        <DrawerFooter>
          {detail?.status === "Pending" && (
            <Button appearance="secondary" onClick={() => { window.location.href = remindHref(detail); }}>Remind approver</Button>
          )}
          {detail && canCancel(detail) && (
            <Button appearance="secondary" onClick={() => setCancelTarget(detail)}>Cancel request</Button>
          )}
          <Button appearance="primary" onClick={() => setDetail(null)}>Close</Button>
        </DrawerFooter>
      </OverlayDrawer>

      <Dialog open={!!cancelTarget} onOpenChange={(_, d) => !d.open && setCancelTarget(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Cancel this leave request?</DialogTitle>
            <DialogContent>
              {cancelTarget && (
                <Text>
                  {cancelTarget.leaveTypeCode} · {formatDateList(cancelTarget.leaveDates)}. If it was approved, the balance is refunded and the calendar entry removed.
                </Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setCancelTarget(null)}>Keep it</Button>
              <Button appearance="primary" disabled={busy} onClick={confirmCancel}>{busy ? "Cancelling…" : "Cancel request"}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(_, d) => { if (!d.open) setPreview(null); }}>
        <DialogSurface className={s.previewSurface}>
          <DialogBody>
            <DialogTitle
              action={<Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={() => setPreview(null)} />}
            >
              {preview?.name ?? "Attachment"}
            </DialogTitle>
            <DialogContent>
              {preview?.loading ? (
                <div className={s.previewCenter}><Spinner label="Loading attachment…" /></div>
              ) : preview?.kind === "image" && preview.src ? (
                <img className={s.previewImg} src={preview.src} alt={preview.name} />
              ) : preview?.kind === "pdf" && preview.src ? (
                <iframe className={s.previewFrame} src={preview.src} title={preview.name} />
              ) : (
                <div className={s.previewCenter}>
                  <Text>This file can’t be previewed here — open it in SharePoint instead.</Text>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              {preview && (
                <Button appearance="secondary" as="a" href={preview.webUrl} target="_blank" rel="noreferrer">
                  Open in SharePoint
                </Button>
              )}
              <Button appearance="primary" onClick={() => setPreview(null)}>Close</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
